<script setup>
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import { useMapDrawingStore } from '@/stores/MapDrawingStore';
  import ToggleInputDefault from "@/components/controls/ToggleInputDefault.vue";
  import ControlButton from '@/components/controls/ControlButton.vue';
  import Icon from '@/components/icons/Icon.vue';

  const props = defineProps({
    isSmallDevice: {
      type: Boolean,
      required: true
    }
  });

  const mapDrawingStore = useMapDrawingStore();

  const mounted = ref(false);
  
  const showDeleteAllButton = computed(() => mapDrawingStore.shapes.length > 0 && mapDrawingStore.shapes[0].points.length > 0);
  
  const showHints = computed(() => {
    const shapeType = mapDrawingStore.selectedDrawingTool;
    const pointCount = mapDrawingStore.activeShape?.points.length || 0;

    // if (!shapeType || !mapDrawingStore.activeShape) {
    //   return false;
    // }

    if (shapeType === 'circle' && pointCount === 2) {
      return false;
    }

    if (shapeType === 'polygon' && mapDrawingStore.activeShape.closed) {
      return false;
    }

    return true;
  });

  const hint = computed(() => {
    if (!mapDrawingStore.activeShape && !mapDrawingStore.selectedDrawingTool) {
      return 'Select a shape tool below to start drawing.';
    }
    
    const pointCount = mapDrawingStore.activeShape.points.length;

    switch (mapDrawingStore.selectedDrawingTool) {
      case 'polyline':
        if (pointCount > 0) {
          return 'Click on map to add more points.';
        }
        else {
          return 'Click on map to add starting point.';
        }
      case 'polygon':
        if (pointCount > 0 && pointCount < 3) {
          return 'Click on map to add another point.';
        }
        else if(pointCount >= 3 && !mapDrawingStore.activeShape.closed) {
          return 'Click on starting point to close or on the map to add another point.';
        }
        else {
          return 'Click on map to add starting point.';
        }
      case 'circle':
        if (pointCount > 0) {
          return 'Click on map or enter a radius to set the circle size.';
        }
        else {
          return 'Click on map to add circle center point.';
        }
      default:
        return 'Select a shape tool below to start drawing.';
    }
  });

  const handleToolSelectionClick = (e) => {
    const selectedTool = e.currentTarget.dataset.tool;

    mapDrawingStore.selectedDrawingTool = selectedTool;
    mapDrawingStore.addShape(selectedTool);
    mapDrawingStore.setDrawingMode('editing');
  };

  const handleDeleteAllShapesClick = () => {
    mapDrawingStore.deleteAllShapes();
    mapDrawingStore.setDrawingMode('viewing');
  };

  const handleDistanceMarkerChange = (e, valueType) => {
    if (valueType === 'distance') {
      mapDrawingStore.showHeadingMarkers = false;
    }
    else if (valueType === 'headings') {
      mapDrawingStore.showDistanceMarkers = false;
    }
  }


  onMounted(() => mounted.value = true);
  onUnmounted(() => document.documentElement.removeAttribute('data-drawing-mode'));
</script>

<template>
    <Teleport to=".jwx-map__bottom-container" :disabled="!props.isSmallDevice">
      <Transition name="slide-fade">
        <div
          v-if="mounted"
          id="jwx-map-drawing-toolbar"
          class="jwx-map__drawing-toolbar jwx-map-block jwx-map-block--row jwx-map-block--transparent-border"
          data-shape="pill"
          data-responsive="false"
        >
          <ul class="jwx-map-block__content jwx-map-block__items">
            <li class="jwx-map-block__item">
              <ControlButton
                @click="handleToolSelectionClick"
                data-tool="polyline"
                :data-selected="mapDrawingStore.selectedDrawingTool === 'polyline'"
              >
                <Icon name="line" class="jwx-button-control__icon" />  
              </ControlButton>
            </li>
            <li class="jwx-map-block__item">
              <ControlButton
                @click="handleToolSelectionClick"
                data-tool="circle"
                :data-selected="mapDrawingStore.selectedDrawingTool === 'circle'"
              >
                <Icon name="circle" class="jwx-button-control__icon" />  
              </ControlButton>
            </li>
            <li class="jwx-map-block__item">
              <ControlButton
                @click="handleToolSelectionClick"
                data-tool="polygon"
                :data-selected="mapDrawingStore.selectedDrawingTool === 'polygon'"
              >
                <Icon name="hexagon" class="jwx-button-control__icon" />  
              </ControlButton>
            </li>
          </ul>
          <ul class="jwx-map-block__content jwx-map-block__items">
            <li class="jwx-map-block__item">
              <ToggleInputDefault
                @input:change="handleDistanceMarkerChange($event, 'distance')"
                :attributes="{
                  inputType: 'checkbox',
                  id: 'drawing-toolbar-show-measurements',
                }"
                name="drawing-toolbar-show-measurements"
                v-model="mapDrawingStore.showDistanceMarkers"
              />
              <label
                @click.stop
                class="jwx-button-control"
                for="drawing-toolbar-show-measurements"
                :data-selected="mapDrawingStore.showDistanceMarkers"
              >
                <Icon name="ruler" class="jwx-button-control__icon" />  
              </label>
            </li>
            <li class="jwx-map-block__item">
              <ToggleInputDefault
                @input:change="handleDistanceMarkerChange($event, 'headings')"
                :attributes="{
                  inputType: 'checkbox',
                  id: 'drawing-toolbar-show-headings',
                }"
                name="drawing-toolbar-show-headings"
                v-model="mapDrawingStore.showHeadingMarkers"
              />
              <label
                @click.stop
                class="jwx-button-control"
                for="drawing-toolbar-show-headings"
                :data-selected="mapDrawingStore.showHeadingMarkers"
              >
                <Icon name="compass" class="jwx-button-control__icon" />  
              </label>
            </li>
          </ul>
          <ul v-if="showDeleteAllButton" class="jwx-map-block__content jwx-map-block__items">
            <li class="jwx-map-block__item">
              <button
                @click="handleDeleteAllShapesClick"
                class="jwx-button-control"
                data-type="danger"
              >
                <Icon name="trash-can-x" class="jwx-button-control__icon" />  
              </button>
            </li>
          </ul>        
          <div v-if="showHints" class="jwx-toolbar__hint">
            <p class="jwx-toolbar__hint-text">{{ hint }}</p>
          </div>
        </div>
      </Transition>
    </Teleport>
</template>